const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
    headers?: Record<string, string>;
}

interface ApiResponse<T = any> {
    data: T;
    status: number;
}

class ApiClient {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private getHeaders(): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        const token = localStorage.getItem('promptpilot_token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    private async request<T>(
        method: string,
        endpoint: string,
        body?: any,
        options?: RequestOptions
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;
        const headers = { ...this.getHeaders(), ...options?.headers };

        const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });

        const data = await response.json();

        if (!response.ok) {
            const error = new Error(data.message || 'Request failed') as any;
            error.response = { data, status: response.status };
            throw error;
        }

        return { data, status: response.status };
    }

    async get<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
        return this.request<T>('GET', endpoint, undefined, options);
    }

    async post<T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
        return this.request<T>('POST', endpoint, body, options);
    }

    async put<T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
        return this.request<T>('PUT', endpoint, body, options);
    }

    async delete<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
        return this.request<T>('DELETE', endpoint, undefined, options);
    }
}

export const api = new ApiClient(API_BASE_URL);
